import type { ColumnDef } from "@tanstack/react-table";
import { Box, Checkbox, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { IconCircleCheck, IconEdit, IconEye, IconQrcode, IconSquareX } from "@tabler/icons-react";
import type { FilaInventario, SeleccionInventario } from "../types/catalogoInventario.types";
import { COLORES_INVENTARIO, ESTATUS_INVENTARIO } from "../constants/catalogoInventario.constants";
import { fecha, moneda, textoOVacio } from "../utils/catalogoInventarioFormato.utils";
import { esSeleccionableQr } from "../utils/qrProducto.utils";

export interface TextosInventario {
    clave: string;
    nombre: string;
    precio: string;
    unidadMedida: string;
    categoria: string;
    cantidad: string;
    maximos: string;
    minimos: string;
    puntoReorden: string;
    ubicacion: string;
    almacen: string;
    fecha: string;
    estatus: string;
    acciones: string;
    ver: string;
    editar: string;
    activar: string;
    desactivar: string;
    yaTieneQr: string;
}

export interface AccionesInventario {
    onVer: (fila: FilaInventario) => void;
    onEditar: (fila: FilaInventario) => void;
    onActivar: (fila: FilaInventario) => void;
    onDesactivar: (fila: FilaInventario) => void;
}

const ANCHO_SELECCION = 48;
const ANCHO_NOMBRE = 220;
const ANCHO_PRECIO = 110;
const ANCHO_TEXTO = 130;
const ANCHO_NUMERO = 90;
const ANCHO_FECHA = 110;
const ANCHO_ESTATUS = 120;
const ANCHO_ACCIONES = 120;

export const ANCHO_MINIMO_INVENTARIO =
    ANCHO_SELECCION + ANCHO_NOMBRE + ANCHO_PRECIO + ANCHO_NUMERO * 4 + ANCHO_TEXTO * 2 +
    ANCHO_FECHA + ANCHO_ESTATUS + ANCHO_ACCIONES;

const celdaTexto = (valor: string) => (
    <Typography variant="caption" sx={{ fontSize: 13, display: "block" }} noWrap title={valor}>
        {textoOVacio(valor)}
    </Typography>
);

const celdaNumero = (valor: number) => (
    <Typography variant="caption" sx={{ fontSize: 13, display: "block" }}>
        {valor}
    </Typography>
);

/**
 * Checkbox para QR masivo. Si el producto ya tiene QR muestra el ícono en lugar del checkbox.
 * El checkbox del encabezado marca/desmarca los seleccionables de la página actual.
 */
const columnaSeleccion = (
    seleccion: SeleccionInventario,
    textoYaTieneQr: string
): ColumnDef<FilaInventario, unknown> => ({
    id: "seleccion",
    enableSorting: false,
    meta: { ancho: ANCHO_SELECCION, alineacion: "center" },
    header: ({ table }) => {
        const ids = table
            .getRowModel()
            .rows.map((r) => r.original)
            .filter(esSeleccionableQr)
            .map((f) => f.id);
        const marcados = ids.filter(seleccion.estaSeleccionado).length;
        return (
            <Checkbox
                size="small"
                disabled={ids.length === 0}
                checked={ids.length > 0 && marcados === ids.length}
                indeterminate={marcados > 0 && marcados < ids.length}
                onChange={(e) => seleccion.marcarVarios(ids, e.target.checked)}
                inputProps={{ "aria-label": "Seleccionar productos de esta página" }}
            />
        );
    },
    cell: ({ row }) => {
        const fila = row.original;
        if (fila.qr) {
            return (
                <Tooltip title={`${textoYaTieneQr}: ${fila.qr.clave}`}>
                    <Box sx={{ display: "flex", justifyContent: "center", color: COLORES_INVENTARIO.qr }}>
                        <IconQrcode size={20} />
                    </Box>
                </Tooltip>
            );
        }
        return (
            <Checkbox
                size="small"
                disabled={!esSeleccionableQr(fila)}
                checked={seleccion.estaSeleccionado(fila.id)}
                onChange={() => seleccion.alternar(fila.id)}
                inputProps={{ "aria-label": `Seleccionar ${fila.nombre}` }}
            />
        );
    },
});

export const construirColumnasInventario = (
    textos: TextosInventario,
    acciones: AccionesInventario,
    seleccion: SeleccionInventario
): ColumnDef<FilaInventario, unknown>[] => [
        columnaSeleccion(seleccion, textos.yaTieneQr),
        {
            accessorKey: "nombre",
            header: textos.nombre,
            meta: { ancho: ANCHO_NOMBRE },
            cell: ({ row }) => (
                <>
                    <Typography variant="h4" fontWeight={600} sx={{ fontSize: 14, display: "block" }}>
                        {row.original.nombre}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: 12, display: "block" }}>
                        {row.original.clave} · {row.original.categoria}
                    </Typography>
                </>
            ),
        },
        {
            accessorKey: "precio",
            header: textos.precio,
            meta: { ancho: ANCHO_PRECIO, alineacion: "right" },
            cell: ({ row }) => (
                <Typography variant="caption" sx={{ fontSize: 13, display: "block" }}>
                    {moneda(row.original.precio)}
                </Typography>
            ),
        },
        {
            accessorKey: "cantidad",
            header: textos.cantidad,
            meta: { ancho: ANCHO_NUMERO, alineacion: "center" },
            cell: ({ row }) => (
                <Box>
                    {celdaNumero(row.original.cantidad)}
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: 12, display: "block" }}
                        noWrap
                        title={row.original.unidadMedida}
                    >
                        {textoOVacio(row.original.unidadMedida)}
                    </Typography>
                </Box>
            ),
        },
        {
            accessorKey: "maximos",
            header: textos.maximos,
            meta: { ancho: ANCHO_NUMERO, alineacion: "center" },
            cell: ({ row }) => celdaNumero(row.original.maximos),
        },
        {
            accessorKey: "minimos",
            header: textos.minimos,
            meta: { ancho: ANCHO_NUMERO, alineacion: "center" },
            cell: ({ row }) => celdaNumero(row.original.minimos),
        },
        {
            accessorKey: "puntoReorden",
            header: textos.puntoReorden,
            meta: { ancho: ANCHO_NUMERO, alineacion: "center" },
            cell: ({ row }) => celdaNumero(row.original.puntoReorden),
        },
        {
            accessorKey: "ubicacion",
            header: textos.ubicacion,
            meta: { ancho: ANCHO_TEXTO },
            cell: ({ row }) => celdaTexto(row.original.ubicacion),
        },
        {
            accessorKey: "almacen",
            header: textos.almacen,
            meta: { ancho: ANCHO_TEXTO },
            cell: ({ row }) => celdaTexto(row.original.almacen),
        },
        {
            accessorKey: "fechaCreacion",
            header: textos.fecha,
            meta: { ancho: ANCHO_FECHA, alineacion: "center" },
            cell: ({ row }) => celdaTexto(fecha(row.original.fechaCreacion)),
        },
        {
            accessorKey: "activo",
            header: textos.estatus,
            meta: { ancho: ANCHO_ESTATUS, alineacion: "center" },
            cell: ({ row }) => {
                const estatus = row.original.activo ? ESTATUS_INVENTARIO.activo : ESTATUS_INVENTARIO.inactivo;
                return (
                    <Chip
                        size="small"
                        label={estatus.label}
                        sx={{
                            fontWeight: 600,
                            fontSize: 10,
                            color: estatus.color,
                            backgroundColor: `${estatus.color}1A`,
                            border: `1px solid ${estatus.color}`,
                        }}
                    />
                );
            },
        },
        {
            id: "acciones",
            header: textos.acciones,
            enableSorting: false,
            meta: { ancho: ANCHO_ACCIONES, alineacion: "center" },
            cell: ({ row }) => {
                const fila = row.original;
                return (
                    <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                        <Tooltip title={textos.ver}>
                            <IconButton size="small" onClick={() => acciones.onVer(fila)}>
                                <IconEye size={18} />
                            </IconButton>
                        </Tooltip>
                        {fila.activo && (
                            <Tooltip title={textos.editar}>
                                <IconButton size="small" color="warning" onClick={() => acciones.onEditar(fila)}>
                                    <IconEdit size={18} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {fila.activo ? (
                            <Tooltip title={textos.desactivar}>
                                <IconButton size="small" color="error" onClick={() => acciones.onDesactivar(fila)}>
                                    <IconSquareX size={18} />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Tooltip title={textos.activar}>
                                <IconButton size="small" color="success" onClick={() => acciones.onActivar(fila)}>
                                    <IconCircleCheck size={18} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                );
            },
        },
    ];
